import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Input, message, Switch, Select, Modal, Form, Space, Popconfirm } from "antd";
import { TrainingAPI } from "../../api/TrainingAPI";
import { PlusOutlined, DeleteOutlined, EditOutlined, EnvironmentOutlined, CloseOutlined } from '@ant-design/icons';
import moment from "moment";

const { Option } = Select;

const INDOOR_TEMP = 70;
const INDOOR_HUMIDITY = 50;

// US zipcode validation regex
const zipCodeRegex = /^\d{5}(-\d{4})?$/;

export const WeatherTable = ({ dates, onSubmit, dateRangeWeather, setDateRangeWeather }) => {
  const [weatherData, setWeatherData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weatherCache, setWeatherCache] = useState({});
  const [presets, setPresets] = useState([]);
  const [addPresetModalVisible, setAddPresetModalVisible] = useState(false);
  const [deletePresetModalVisible, setDeletePresetModalVisible] = useState(false);
  const [customLocationModalVisible, setCustomLocationModalVisible] = useState(false);
  const [currentCustomLocationRow, setCurrentCustomLocationRow] = useState(null);
  const [selectedPresetForDelete, setSelectedPresetForDelete] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addPresetForm] = Form.useForm();
  const [customLocationForm] = Form.useForm();
  
  const trainingAPI = new TrainingAPI();

  // Fetch presets from API
  const fetchPresets = useCallback(async () => {
    try {
      const presetsData = await trainingAPI.getWeatherPresets();
      setPresets(presetsData);
      setRefreshKey(prevKey => prevKey + 1);
      return presetsData;
    } catch (error) {
      console.error('Error fetching weather presets:', error);
      message.error("Failed to fetch weather presets");
      return [];
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchWeatherData = async (zipcode) => {
    try {
      setLoading(true);
      
      // Format the dates for the API
      const startDate = moment(dates[0]).format('YYYY-MM-DD');
      const endDate = moment(dates[1]).format('YYYY-MM-DD');
      
      // Create a cache key based on location and date range
      const cacheKey = `weather_${zipcode}_${startDate}_${endDate}`;
      
      // Try to get cached data
      const cachedData = sessionStorage.getItem(cacheKey);
      let weatherResponse;
      
      if (cachedData) {
        weatherResponse = JSON.parse(cachedData);
      } else {
        weatherResponse = await trainingAPI.getWeatherData(zipcode, startDate, endDate);
        // Cache the response
        sessionStorage.setItem(cacheKey, JSON.stringify(weatherResponse));
      }

      return weatherResponse;
    } catch (error) {
      console.error('Weather API Error:', error);
      message.error("Failed to fetch weather data");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleDayWeather = async (zipcode, date) => {
    try {
      setLoading(true);
      
      // Create a cache key for this specific date/location
      const cacheKey = `weather_${zipcode}_${date}`;
      
      // Try to get cached data
      const cachedData = sessionStorage.getItem(cacheKey);
      let weatherResponse;
      
      if (cachedData) {
        weatherResponse = JSON.parse(cachedData);
      } else {
        weatherResponse = await trainingAPI.getWeatherData(zipcode, date);
        // Cache the response
        sessionStorage.setItem(cacheKey, JSON.stringify(weatherResponse));
      }

      return weatherResponse;
    } catch (error) {
      console.error('Weather API Error:', error);
      message.error("Failed to fetch weather data");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const initializeWeatherData = async () => {
    if (presets.length === 0) {
      message.warning("No weather location presets found. Please add a preset first.");
      return;
    }

    // Use first preset as default
    const defaultPreset = presets[0];
    const weatherData = await fetchWeatherData(defaultPreset.zipcode);

    if (!weatherData) return;

    // Store weather data for default preset
    const cacheData = {};
    cacheData[defaultPreset.zipcode] = weatherData;
    setWeatherCache(cacheData);

    // Initialize the table data with default values
    const initialData = weatherData.days.map(day => {
      const defaultTime = "12:00";
      const hour = parseInt(defaultTime.split(':')[0]);
      const weatherForHour = day.hours.find(h => 
        parseInt(h.datetime.split(':')[0]) === hour
      ) || day.hours[0];

      return {
        key: day.datetime,
        date: day.datetime,
        time: defaultTime,
        presetName: defaultPreset.name,
        presetZipcode: defaultPreset.zipcode,
        isIndoor: false,
        temperature: weatherForHour.temp,
        humidity: weatherForHour.humidity,
        isCustomLocation: false
      };
    });

    setWeatherData(initialData);
  };

  useEffect(() => {
    if (dates && dates.length === 2) {
      const daysDiff = moment(dates[1]).diff(moment(dates[0]), 'days');
      if (daysDiff > 14) {
        onSubmit([]);
        return;
      }
      if (presets.length > 0) {
        initializeWeatherData();
      }
    }
  }, [dates, presets]);

  const updateWeatherForRow = (row, newPresetName, newPresetZipcode, newIsIndoor) => {
    if (newIsIndoor) {
      return {
        ...row,
        temperature: INDOOR_TEMP,
        humidity: INDOOR_HUMIDITY
      };
    }

    const locationData = weatherCache[newPresetZipcode];
    if (!locationData) return row;

    // Handle both single-day and multi-day weather data structures
    const dayData = locationData.days ? 
      locationData.days.find(d => d.datetime === row.date) :
      locationData;

    if (!dayData) return row;

    const hour = parseInt(row.time.split(':')[0]);
    const weatherForHour = dayData.hours.find(h => 
      parseInt(h.datetime.split(':')[0]) === hour
    ) || dayData.hours[0];

    return {
      ...row,
      temperature: weatherForHour.temp,
      humidity: weatherForHour.humidity
    };
  };

  const handleLocationChange = async (date, value) => {
    // Check if this is the custom location option
    if (value === 'custom') {
      // Find the row we're changing and set it as the current custom location row
      const currentRow = weatherData.find(item => item.date === date);
      setCurrentCustomLocationRow(currentRow);
      setCustomLocationModalVisible(true);
      return;
    }
    
    const [presetName, presetZipcode] = value.split('|');
    
    try {
      setLoading(true);
      let locationWeatherData;
      
      // Create a specific cache key for this location and date
      const cacheKey = `weather_${presetZipcode}_${date}`;
      
      // Check if we have cached data for this specific location and date
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        locationWeatherData = JSON.parse(cachedData);
      } else {
        // Check if we have multi-day data for this location in weatherCache
        if (weatherCache[presetZipcode] && weatherCache[presetZipcode].days) {
          // We have multi-day data, try to find this specific date
          const dayData = weatherCache[presetZipcode].days.find(d => d.datetime === date);
          if (dayData) {
            locationWeatherData = { days: [dayData] };
          }
        }
        
        // If we still don't have data, fetch it from the server
        if (!locationWeatherData) {
          locationWeatherData = await fetchSingleDayWeather(presetZipcode, date);
          if (!locationWeatherData) {
            message.error(`Failed to fetch weather data for ${presetName}`);
            return;
          }
          
          // Cache this specific date's data
          sessionStorage.setItem(cacheKey, JSON.stringify(locationWeatherData));
        }
      }

      // Update the row with the new weather data
      const updatedData = weatherData.map(item => {
        if (item.date === date) {
          // Get the current hour's weather data
          const hour = parseInt(item.time.split(':')[0]);
          let weatherForHour;
          
          if (locationWeatherData.days && locationWeatherData.days[0]) {
            // Multi-day response format
            weatherForHour = locationWeatherData.days[0].hours.find(h => 
              parseInt(h.datetime.split(':')[0]) === hour
            ) || locationWeatherData.days[0].hours[0];
          } else if (locationWeatherData.hours) {
            // Single-day response format
            weatherForHour = locationWeatherData.hours.find(h => 
              parseInt(h.datetime.split(':')[0]) === hour
            ) || locationWeatherData.hours[0];
          } else {
            // Fallback to first available hour data
            weatherForHour = locationWeatherData;
          }

          return {
            ...item,
            presetName: presetName,
            presetZipcode: presetZipcode,
            isCustomLocation: false,
            temperature: item.isIndoor ? INDOOR_TEMP : weatherForHour.temp,
            humidity: item.isIndoor ? INDOOR_HUMIDITY : weatherForHour.humidity
          };
        }
        return item;
      });
      
      setWeatherData(updatedData);
    } catch (error) {
      console.error('Error updating location:', error);
      message.error('Failed to update weather data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomLocationSubmit = async () => {
    try {
      const values = await customLocationForm.validateFields();
      
      // Validate zipcode format
      if (!zipCodeRegex.test(values.zipcode)) {
        message.error("Please enter a valid US zipcode (e.g., 12345 or 12345-6789)");
        return;
      }
      
      // Validate name length
      if (values.name.length > 100) {
        message.error("Location name must be less than 100 characters");
        return;
      }
      
      if (!currentCustomLocationRow) {
        message.error("Error identifying the row to update");
        return;
      }
      
      // Add the location as a preset
      await trainingAPI.addWeatherPreset(values.zipcode, values.name);
      
      // Refresh presets to include the new one
      await fetchPresets();
      
      // Fetch weather data for the custom location
      const data = await fetchWeatherData(values.zipcode);
      if (data) {
        // Update the weather cache with this custom location
        setWeatherCache(prev => ({
          ...prev,
          [values.zipcode]: data
        }));
        
        // Update the weather data row
        const updatedData = weatherData.map(item => {
          if (item.date === currentCustomLocationRow.date) {
            const dayData = data.days.find(d => d.datetime === item.date);
            if (!dayData) return item;
            
            const hour = parseInt(item.time.split(':')[0]);
            const weatherForHour = dayData.hours.find(h => 
              parseInt(h.datetime.split(':')[0]) === hour
            ) || dayData.hours[0];
            
            return {
              ...item,
              presetName: values.name,
              presetZipcode: values.zipcode,
              temperature: item.isIndoor ? item.temperature : weatherForHour.temp,
              humidity: item.isIndoor ? item.humidity : weatherForHour.humidity,
              isCustomLocation: false
            };
          }
          return item;
        });
        
        setWeatherData(updatedData);
        
        // Close modal and reset form
        setCustomLocationModalVisible(false);
        customLocationForm.resetFields();
        setCurrentCustomLocationRow(null);
        
        message.success(`Weather location "${values.name}" added and updated`);
      } else {
        message.error(`Failed to fetch weather data for ${values.name}`);
      }
    } catch (error) {
      console.error('Error with custom location:', error);
      message.error("Failed to add custom location");
    }
  };

  const handleIndoorChange = (date, checked) => {
    const updatedData = weatherData.map(item => {
      if (item.date === date) {
        const updatedRow = updateWeatherForRow(
          item, 
          item.presetName, 
          item.presetZipcode, 
          checked
        );
        return {
          ...updatedRow,
          isIndoor: checked
        };
      }
      return item;
    });
    setWeatherData(updatedData);
  };

  const handleTimeChange = (date, newTime) => {
    const updatedData = weatherData.map(item => {
      if (item.date === date) {
        if (item.isIndoor) {
          return {
            ...item,
            time: newTime
          };
        }
        return updateWeatherForRow(
          { ...item, time: newTime }, 
          item.presetName,
          item.presetZipcode, 
          false
        );
      }
      return item;
    });
    setWeatherData(updatedData);
  };

  const handleTemperatureChange = (date, value) => {
    const updatedData = weatherData.map(item => 
      item.date === date ? { ...item, temperature: value } : item
    );
    setWeatherData(updatedData);
  };

  const handleHumidityChange = (date, value) => {
    const updatedData = weatherData.map(item => 
      item.date === date ? { ...item, humidity: value } : item
    );
    setWeatherData(updatedData);
  };

  const handleAddPreset = async () => {
    try {
      const values = await addPresetForm.validateFields();
      
      // Validate zipcode format
      if (!zipCodeRegex.test(values.zipcode)) {
        message.error("Please enter a valid US zipcode (e.g., 12345 or 12345-6789)");
        return;
      }
      
      // Validate name length
      if (values.name.length > 100) {
        message.error("Location name must be less than 100 characters");
        return;
      }
      
      // Call API to add preset
      await trainingAPI.addWeatherPreset(values.zipcode, values.name);
      
      // Refresh presets
      fetchPresets();
      
      // Close modal and reset form
      setAddPresetModalVisible(false);
      addPresetForm.resetFields();
      
      message.success("Weather location preset added successfully");
    } catch (error) {
      console.error('Error adding preset:', error);
      message.error("Failed to add weather location preset");
    }
  };

  // Delete preset function
  const deletePreset = async (preset) => {
    try {
      // Log for debugging
      console.log("Deleting preset:", preset);
      
      // Make the API call and wait for it to complete
      const response = await trainingAPI.deleteWeatherPreset(preset.zipcode);
      console.log("Delete API response:", response);
      
      // Manually remove the item from the local state for immediate feedback
      setPresets(currentPresets => 
        currentPresets.filter(p => p.zipcode !== preset.zipcode)
      );
      
      // Then fetch fresh data in the background
      setTimeout(() => {
        fetchPresets();
      }, 100);
      
      message.success("Weather location preset deleted successfully");
    } catch (error) {
      console.error('Error deleting preset:', error);
      message.error("Failed to delete weather location preset");
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedPresetForDelete) return;
    await deletePreset(selectedPresetForDelete);
    setSelectedPresetForDelete(null);
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "12%",
      render: (date) => <span>{date}</span>
    },
    {
      title: "Location",
      dataIndex: "presetName",
      key: "presetName",
      width: "35%",
      render: (presetName, record) => (
        <Select
          value={record.isCustomLocation ? record.presetName : `${record.presetName}|${record.presetZipcode}`}
          style={{ 
            width: '100%',
            textAlign: 'left'
          }}
          onChange={(value) => handleLocationChange(record.date, value)}
          optionLabelProp="label"
          dropdownStyle={{ textAlign: 'left' }}
        >
          <Option value="custom" label="Add Custom Location" style={{ fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
            <EnvironmentOutlined /> Add Custom Location
          </Option>
          {presets.map((preset, index) => (
            <Option 
              key={preset.zipcode} 
              value={`${preset.name}|${preset.zipcode}`}
              label={`${preset.name} | ${preset.zipcode}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>{preset.name} | {preset.zipcode}</span>
                {index > 0 && (
                  <Popconfirm
                    title="Delete Location Preset"
                    description={`Are you sure you want to delete "${preset.name}"?`}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      deletePreset(preset);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <CloseOutlined 
                      style={{ 
                        color: 'red', 
                        cursor: 'pointer',
                        padding: '4px',
                        fontSize: '12px',
                        marginLeft: '8px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  </Popconfirm>
                )}
              </div>
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: "Indoor",
      dataIndex: "isIndoor",
      key: "isIndoor",
      width: "10%",
      render: (isIndoor, record) => (
        <Switch
          checked={isIndoor}
          onChange={(checked) => handleIndoorChange(record.date, checked)}
          checkedChildren="Indoor"
          unCheckedChildren="Outdoor"
        />
      )
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      width: "15%",
      render: (time, record) => (
        <Input
          type="time"
          value={time}
          onChange={(e) => handleTimeChange(record.date, e.target.value)}
        />
      )
    },
    {
      title: "Temperature (°F)",
      dataIndex: "temperature",
      key: "temperature",
      width: "14%",
      render: (temperature, record) => (
        <Input
          type="number"
          value={temperature}
          onChange={(e) => handleTemperatureChange(record.date, e.target.value)}
        />
      )
    },
    {
      title: "Humidity (%)",
      dataIndex: "humidity",
      key: "humidity",
      width: "14%",
      render: (humidity, record) => (
        <Input
          type="number"
          value={humidity}
          onChange={(e) => handleHumidityChange(record.date, e.target.value)}
        />
      )
    }
  ];

  const handleSubmit = () => {
    // Transform data to the format expected by onSubmit
    const formattedData = weatherData.map(item => ({
      date: item.date,
      temperature: item.temperature,
      humidity: item.humidity
    }));
    
    onSubmit(formattedData);
  };

  const handleSkip = () => {
    onSubmit([]);
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: '14px', marginBottom: '16px' }}>
          Please provide the following information for each training date. You can use saved location presets or add new locations. You may customize the temperature and humidity for each date.
        </p>
      </div>
      
      <Table
        columns={columns}
        dataSource={weatherData}
        loading={loading}
        pagination={weatherData.length > 7 ? {
          pageSize: 7,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} days`,
          position: ['bottomLeft']
        } : false}
        size="small"
      />
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, marginBottom: 16 }}>
        <Button type="primary" onClick={handleSubmit}>
          Submit Weather Data
        </Button>
      </div>
      
      {/* Custom Location Modal */}
      <Modal
        title="Add New Location"
        open={customLocationModalVisible}
        onOk={handleCustomLocationSubmit}
        onCancel={() => {
          setCustomLocationModalVisible(false);
          customLocationForm.resetFields();
          setCurrentCustomLocationRow(null);
        }}
      >
        <Form form={customLocationForm} layout="vertical">
          <Form.Item
            name="name"
            label="Location Name"
            rules={[
              { required: true, message: "Please enter a location name" },
              { max: 100, message: "Name must be less than 100 characters" }
            ]}
          >
            <Input placeholder="e.g., Practice Facility" />
          </Form.Item>
          <Form.Item
            name="zipcode"
            label="Zipcode"
            rules={[
              { required: true, message: "Please enter a zipcode" },
              { 
                pattern: zipCodeRegex, 
                message: "Please enter a valid US zipcode (e.g., 12345 or 12345-6789)" 
              }
            ]}
          >
            <Input placeholder="e.g., 89052" />
          </Form.Item>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '-12px' }}>
            This location will be saved as a preset for future use.
          </p>
        </Form>
      </Modal>
    </>
  );
}; 